import { useState, useRef, useEffect } from 'react';
import { Eye, Edit, ShieldOff, ShieldCheck, Trash2, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PartnerActionsMenu = ({ partner, onView, onEdit, onFreeze, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 220) {
        setOpenUpwards(true);
      } else {
        setOpenUpwards(false);
      }
    }
  }, [isOpen]);

  const actions = [
    {
      icon: <Eye size={15} />,
      label: 'View Details',
      onClick: () => {
        setIsOpen(false);
        if (onView) onView(partner);
      },
      isDelete: false
    },
    {
      icon: <Edit size={15} />,
      label: 'Edit Partner',
      onClick: () => {
        setIsOpen(false);
        if (onEdit) onEdit(partner);
      },
      isDelete: false
    },
    {
      icon: partner.isActive !== false ? <ShieldOff size={15} /> : <ShieldCheck size={15} />,
      label: partner.isActive !== false ? 'Freeze Partner' : 'Unfreeze Partner',
      onClick: () => {
        setIsOpen(false);
        if (onFreeze) onFreeze(partner);
      },
      isDelete: false
    },
    {
      icon: <Trash2 size={15} />,
      label: 'Delete Partner',
      onClick: () => {
        setIsOpen(false);
        if (onDelete) onDelete(partner);
      },
      isDelete: true
    }
  ];

  return (
    <div className={`relative ${isOpen ? 'z-50' : ''}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-xs font-semibold ${
          isOpen
            ? 'bg-[#067978] text-white shadow-lg shadow-[#067978]/20 scale-95'
            : 'text-slate-500 bg-slate-50 border border-slate-100 hover:text-slate-700 hover:bg-slate-100'
        } focus:outline-none cursor-pointer`}
        title="Options"
      >
        <span>Options</span>
        <ChevronRight size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: openUpwards ? 15 : -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: openUpwards ? 15 : -15 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`absolute right-0 w-48 bg-white rounded-xl shadow-xl border border-slate-200/60 py-1.5 z-[1000] ${
              openUpwards ? 'bottom-full mb-2 origin-bottom-right' : 'top-full mt-2 origin-top-right'
            }`}
          >
            {actions.map((action, idx) => (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                className={`w-full group flex items-center gap-3 px-4 py-2 text-[13px] font-medium transition-all cursor-pointer ${
                  action.isDelete
                    ? 'text-slate-600 hover:bg-rose-50 hover:text-rose-600'
                    : 'text-slate-600 hover:bg-[#067978]/5 hover:text-[#067978]'
                }`}
              >
                <span
                  className={`transition-all duration-200 ${
                    action.isDelete
                      ? 'text-rose-500 group-hover:scale-110'
                      : 'text-[#067978] opacity-80 group-hover:opacity-100 group-hover:scale-110'
                  }`}
                >
                  {action.icon}
                </span>
                <span className="transition-transform duration-200">{action.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartnerActionsMenu;
